/**
 * /frontend/src/features/analytics/hooks/useAnalyticsResource.js
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApiRequest from '../../../shared/hooks/useApiRequest';

const DEDUP_WINDOW_MS = 2_000;

/**
 * Standard analytics hook wrapper: fetches data, exposes loading/error and refetch.
 * @param {boolean} enabled
 * @param {Function} fetcher
 * @param {string} logModule
 * @param {unknown} refreshSignal
 * @returns {{data:any,isLoading:boolean,isError:boolean,error:any,refetch:Function}}
 */
const useAnalyticsResource = (enabled, fetcher, logModule, refreshSignal) => {
  const { execute, loading, error } = useApiRequest();
  const [data, setData] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const fetcherRef = useRef(fetcher);
  const lastFetchRef = useRef({ fetcher: null, timestamp: 0 });

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const handleSuccess = useCallback((payload) => {
    setData(payload);
    setHasLoadedOnce(true);
  }, []);

  const handleError = useCallback(() => {
    setHasLoadedOnce(true);
  }, []);

  const fetchInternal = useCallback(
    (force = false) => {
      if (!force) {
        const last = lastFetchRef.current;
        const sameFetcher = last.fetcher === fetcherRef.current;
        const withinWindow = Date.now() - last.timestamp < DEDUP_WINDOW_MS;
        if (sameFetcher && withinWindow) {
          return Promise.resolve();
        }
        lastFetchRef.current = { fetcher: fetcherRef.current, timestamp: Date.now() };
      }
      return execute(() => fetcherRef.current(), {
        logModule,
        onSuccess: (payload) => {
          lastFetchRef.current = { fetcher: fetcherRef.current, timestamp: Date.now() };
          handleSuccess(payload);
        },
        onError: (err) => {
          lastFetchRef.current = { fetcher: fetcherRef.current, timestamp: Date.now() };
          handleError(err);
        },
      });
    },
    [execute, handleError, handleSuccess, logModule]
  );

  const refetch = useCallback(() => fetchInternal(true), [fetchInternal]);

  useEffect(() => {
    if (enabled) {
      fetchInternal(false).catch(() => null);
    }
  }, [enabled, refreshSignal, fetchInternal]);

  return useMemo(() => ({
    data,
    isLoading: loading && !hasLoadedOnce,
    isRefreshing: loading && hasLoadedOnce,
    isError: Boolean(error),
    error,
    refetch
  }), [data, error, hasLoadedOnce, loading, refetch]);
};

export default useAnalyticsResource;
